import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image } from 'antd';
import classNames from 'classnames';
import styles from './index.module.less';

export interface MediaListImage {
    icon?: string;
    origin?: string;
    small?: string;
    hasMask?: boolean;
    maskInfo?: {
        text?: string;
    };
}

export interface MediaListContent {
    images?: MediaListImage[];
    videos?: Array<{
        origin?: string;
        thumb?: string;
        width?: number;
        height?: number;
    }>;
    sourceLinks?: Array<{
        title?: string;
        avatarList?: string[];
        source?: string;
        icon?: string;
        url?: string;
    }>;
}

interface MediaListProps {
    media: MediaListContent;
}

const SCROLL_STEP = 300;
// 判断滚动位置的容错像素
const SCROLL_EDGE_TOLERANCE = 2;

const getPreviewUrl = (image: MediaListImage) => image.origin || image.small || image.icon || '';
const getThumbUrl = (image: MediaListImage) => image.icon || image.small || image.origin || '';

const ScrollArrow: React.FC<{ direction: 'left' | 'right' }> = ({ direction }) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d={direction === 'right' ? 'M5 3l5 4-5 4V3z' : 'M9 3L4 7l5 4V3z'}
            fill="#272933"
        />
    </svg>
);

const MediaList: React.FC<MediaListProps> = ({ media }) => {
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [unmaskedIndexes, setUnmaskedIndexes] = useState<Record<number, boolean>>({});
    const [showLeftBtn, setShowLeftBtn] = useState(false);
    const [showRightBtn, setShowRightBtn] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    const images = useMemo(() => media.images?.filter((image) => getThumbUrl(image)) || [], [media.images]);
    const previewItems = useMemo(() => images.map(getPreviewUrl).filter(Boolean), [images]);
    const videos = media.videos?.filter((video) => video.origin) || [];
    const sourceLinks = media.sourceLinks?.filter((link) => link.url || link.title) || [];

    // 更新左右滑动按钮的显示状态
    const updateScrollButtons = useCallback(() => {
        const el = listRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const canScroll = scrollWidth - clientWidth > SCROLL_EDGE_TOLERANCE;
        setShowLeftBtn(canScroll && scrollLeft > SCROLL_EDGE_TOLERANCE);
        setShowRightBtn(canScroll && scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_TOLERANCE);
    }, []);

    // 图片数量或尺寸变化时重新检测
    useEffect(() => {
        updateScrollButtons();
    }, [images.length, updateScrollButtons]);

    // 监听容器尺寸变化
    useEffect(() => {
        const el = listRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => updateScrollButtons());
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateScrollButtons]);

    if (images.length === 0 && videos.length === 0 && sourceLinks.length === 0) return null;

    const openPreview = (index: number) => {
        setPreviewIndex(index);
        setPreviewVisible(true);
    };

    const handleScrollBy = (delta: number) => {
        listRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
    };

    return (
        <>
            <div className={styles.mediaResources}>
                {images.length > 0 && (
                    <div className={styles.mediaListWrapper}>
                        <div
                            ref={listRef}
                            className={classNames(styles.mediaList, {
                                [styles.singleList]: images.length === 1,
                            })}
                            onScroll={updateScrollButtons}
                        >
                            {images.map((image, index) => {
                                const hasSensitiveMask = image.hasMask && !unmaskedIndexes[index];
                                const thumbUrl = getThumbUrl(image);

                                return (
                                    <div
                                        className={classNames(styles.mediaItem, {
                                            [styles.singleItem]: images.length === 1,
                                        })}
                                        key={`${thumbUrl}_${index}`}
                                    >
                                        <img
                                            src={thumbUrl}
                                            alt=""
                                            className={styles.mediaImage}
                                            onClick={() => openPreview(index)}
                                            onLoad={updateScrollButtons}
                                        />
                                        {hasSensitiveMask && (
                                            <button
                                                className={styles.sensitiveMask}
                                                type="button"
                                                onClick={() =>
                                                    setUnmaskedIndexes((prev) => ({
                                                        ...prev,
                                                        [index]: true,
                                                    }))
                                                }
                                            >
                                                <span>{image.maskInfo?.text || '图片可能包含敏感内容'}</span>
                                                <span className={styles.maskAction}>查看</span>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {showLeftBtn && (
                            <button
                                type="button"
                                className={`${styles.scrollBtn} ${styles.scrollBtnLeft}`}
                                onClick={() => handleScrollBy(-SCROLL_STEP)}
                                aria-label="向左滑动"
                            >
                                <ScrollArrow direction="left" />
                            </button>
                        )}
                        {showRightBtn && (
                            <button
                                type="button"
                                className={`${styles.scrollBtn} ${styles.scrollBtnRight}`}
                                onClick={() => handleScrollBy(SCROLL_STEP)}
                                aria-label="向右滑动"
                            >
                                <ScrollArrow direction="right" />
                            </button>
                        )}
                    </div>
                )}

                {videos.map((video, index) => (
                    <video
                        key={`${video.origin}_${index}`}
                        className={styles.video}
                        controls
                        preload="metadata"
                        poster={video.thumb}
                        src={video.origin}
                    />
                ))}

                {sourceLinks.length > 0 && (
                    <div className={styles.sourceLinks}>
                        {sourceLinks.map((link, index) => (
                            <button
                                key={`${link.title || link.url}_${index}`}
                                className={styles.sourceLink}
                                type="button"
                                onClick={() => {
                                    if (link.url) {
                                        window.open(decodeURIComponent(link.url), '_blank', 'noopener,noreferrer');
                                    }
                                }}
                            >
                                {link.icon && <img src={link.icon} className={styles.sourceIcon} alt="" />}
                                <span className={styles.sourceBody}>
                                    <span className={styles.sourceTitle}>{link.title || link.url}</span>
                                    {link.source && <span className={styles.sourceDesc}>{link.source}</span>}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {previewItems.length > 0 && (
                <Image.PreviewGroup
                    items={previewItems}
                    preview={{
                        visible: previewVisible,
                        current: previewIndex,
                        onChange: setPreviewIndex,
                        onVisibleChange: setPreviewVisible,
                    }}
                />
            )}
        </>
    );
};

export default memo(MediaList);
